/**
 * Primary sidebar navigation item.
 *
 * Thin wrapper around the shared NavItem that adds badge, shortcut,
 * and chevron support specific to the main app sidebar.
 */

import './nav-badge-animations.css';

import { ChevronRight, Lock01 } from '@untitledui/icons';
import type { FC, HTMLAttributes } from 'react';

import { NavGroupHeader, NavItem as NavItemBase } from '@/lib/base/nav';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { usePulseClass } from './usePulseClass';

export interface NavItemProps {
  /** Display label. */
  label: string;
  /** URL to navigate to. */
  href?: string;
  /** Icon component to display. */
  icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
  /** Badge to display. */
  badge?: string | number;
  /** Keyboard shortcut key to display. */
  shortcut?: string;
  /** Whether to show the shortcut badge (controlled by modifier key state). */
  showShortcut?: boolean;
  /** Whether to render a trailing chevron. */
  showChevron?: boolean;
  /** Whether this item is a child (indented). */
  isChild?: boolean;
  /** Whether to render as a non-interactive section header. */
  isSectionHeader?: boolean;
  /** Whether this item is the currently active route. */
  current?: boolean;
  /** Handlers for prefetching content on user intent. */
  onPrefetch?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onMouseDown?: () => void;
  };
  /** Tailwind background color class for pipeline icon (e.g. 'bg-brand-600'). */
  pipelineColorClass?: string;
  /** Number that increments when the caller wants to replay the badge pulse animation. */
  pulseKey?: number;
  /** data-component handle for the badge span (e2e). */
  badgeDataComponent?: string;
  /** When true, renders a trailing lock icon and "Requires a full seat" tooltip. */
  locked?: boolean;
}

export const NavItem = ({
  label,
  href,
  icon,
  badge,
  shortcut,
  showShortcut,
  showChevron,
  isChild,
  isSectionHeader,
  current,
  onPrefetch,
  pipelineColorClass,
  pulseKey,
  badgeDataComponent,
  locked,
}: NavItemProps) => {
  const isPulsing = usePulseClass(pulseKey, 1000);

  if (isSectionHeader) {
    // NavGroupHeader applies uppercase CSS so source strings stay natural-case.
    return <NavGroupHeader label={label} />;
  }

  const badgeElement = badge !== undefined && (
    // h-5 + leading-none locks the badge height to the label's text-sm
    // line-height (20px) so it cannot push the row taller — combined with
    // min-h-9 on the row, this keeps every nav item at a consistent height
    // regardless of badge presence.
    <span
      aria-hidden='true'
      data-component={badgeDataComponent}
      className={cx(
        'inline-flex h-5 items-center rounded-md bg-primary px-1.5 text-xs font-medium leading-none text-secondary shadow-xs ring-1 ring-secondary ring-inset',
        isPulsing && 'nav-badge-pulse'
      )}
    >
      {badge}
    </span>
  );

  const shortcutElement = shortcut && showShortcut && (
    <kbd className='hidden rounded border border-secondary bg-bg-secondary px-1 py-0.5 text-xs font-normal text-tertiary lg:inline-flex'>
      {shortcut}
    </kbd>
  );

  const chevronElement = showChevron && (
    <ChevronRight
      aria-hidden='true'
      className='size-4 shrink-0 text-fg-quaternary'
    />
  );

  const lockElement = locked && (
    <Lock01
      aria-hidden='true'
      className='size-3.5 shrink-0 text-fg-quaternary'
    />
  );

  const trailing = (
    <>
      {badgeElement}
      {shortcutElement}
      {chevronElement}
      {lockElement}
    </>
  );

  const item = (
    <NavItemBase
      label={label}
      href={href}
      icon={icon}
      isChild={isChild}
      current={current}
      onPrefetch={onPrefetch}
      pipelineColorClass={pipelineColorClass}
      trailing={trailing}
    />
  );

  if (locked) {
    return (
      <Tooltip title='Requires a full seat' placement='right'>
        {item}
      </Tooltip>
    );
  }

  return item;
};
